// Marketing (poster program) — admin review panel, twin of ExpensesPanel.
// Monthly tiles, searchable records table with photo View badges and
// expense-style status pills, per-row approve/edit, managed site + location
// type lists, and access-code generation/revocation.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Image as ImageIcon, MapPin, FileDown, Calendar, SlidersHorizontal, Copy } from "lucide-react";
import { getToken } from "@/lib/consult/authSession";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#1F3A5F";
const GREEN = "#2E7D5B";
const DARK_GREEN = "#0F3B2E";
const MUTED = "#6B7280";
const TEXT = "#1F2937";
const BORDER = "#E3E0D9";
const RED = "#B4351B";
const AMBER = "#E58A2E";

const api = axios.create({ baseURL: `${BACKEND}/api/marketing` });
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers["X-Auth-Token"] = t;
  return config;
});

const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
// Formats a typed phone number live as (555) 555-9999 (digits only, 10 max) —
// same convention as the locker phone fields.
function fmtPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length > 0) return `(${d}`;
  return "";
}
const monthLabel = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString("en-CA", { month: "short", year: "numeric" });
const fmtDay = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");

const label = { display: "block", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: MUTED, marginBottom: 4, paddingLeft: 11 };
const input = { width: "100%", height: 38, padding: "0 10px", fontSize: 13, border: `1px solid ${BORDER}`, borderRadius: 8, boxSizing: "border-box", background: "#fff", fontFamily: "inherit", color: TEXT };
const btn = (bg) => ({ height: 36, padding: "0 14px", background: bg, color: "#fff", border: "none", borderRadius: 9, fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" });
const btnGhost = { height: 36, padding: "0 14px", background: "#fff", color: NAVY, border: `1px solid ${BORDER}`, borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
// Ghost button with icon + label locked to one line (used for the bottom actions).
const btnGhostRow = { ...btnGhost, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, whiteSpace: "nowrap" };

function Modal({ title, onClose, children, testId }) {
  const savedScroll = useRef(0);
  useEffect(() => {
    savedScroll.current = window.scrollY;
    return () => window.scrollTo(0, savedScroll.current);
  }, []);
  return (
    <div data-testid={testId} onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, padding: 20, maxWidth: 460, width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>{title}</div>
          <button data-testid={`${testId}-close`} onClick={onClose}
            style={{ marginLeft: "auto", background: "none", border: "none", fontSize: 18, cursor: "pointer", color: MUTED }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { bg: "rgba(229,138,46,0.10)", fg: AMBER, t: "Pending" },
    approved: { bg: "rgba(46,125,91,0.14)", fg: GREEN, t: "Approved" },
  };
  const s = map[status] || map.pending;
  return <span data-testid={`mkt-status-${status}`} style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", background: s.bg, color: s.fg }}>{s.t}</span>;
}

// Compact add / rename / delete editor for the Type and City lists — opened
// from the manage button beside each dropdown, keeping the forms clean.
function ListManager({ title, items, testPrefix, onClose, onAdd, onRename, onDelete }) {
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  return (
    <Modal title={title} onClose={onClose} testId={`${testPrefix}-dialog`}>
      {items.length === 0 && <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>Nothing here yet — add one below.</div>}
      {items.map((it) => (
        <div key={it.id} data-testid={`${testPrefix}-row-${it.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 2px", borderBottom: "1px solid #EFEDE8" }}>
          {editId === it.id ? (
            <>
              <input data-testid={`${testPrefix}-rename-input`} autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                style={{ ...input, flex: 1, minWidth: 0, height: 34 }} />
              <button data-testid={`${testPrefix}-rename-save`} style={{ ...btn(GREEN), height: 34 }} disabled={busy}
                onClick={() => run(async () => { await onRename(it, editName); setEditId(null); })}>Save</button>
              <button data-testid={`${testPrefix}-rename-cancel`} style={{ ...btnGhost, height: 34 }} onClick={() => setEditId(null)}>Cancel</button>
            </>
          ) : (
            <>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: TEXT }}>{it.name}</span>
              <button data-testid={`${testPrefix}-edit-${it.id}`} title="Rename" onClick={() => { setEditId(it.id); setEditName(it.name); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, padding: 4 }}><Pencil style={{ width: 13, height: 13 }} /></button>
              <button data-testid={`${testPrefix}-delete-${it.id}`} title="Delete" onClick={() => run(() => onDelete(it))}
                style={{ background: "none", border: "none", cursor: "pointer", color: RED, padding: 4 }}><Trash2 style={{ width: 13, height: 13 }} /></button>
            </>
          )}
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <input data-testid={`${testPrefix}-new`} value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && newName.trim() && !busy && run(async () => { await onAdd(newName); setNewName(""); })}
          placeholder="New name…" style={{ ...input, flex: 1, minWidth: 0 }} />
        <button data-testid={`${testPrefix}-add`} style={btn(GREEN)} disabled={busy || !newName.trim()}
          onClick={() => run(async () => { await onAdd(newName); setNewName(""); })}>Add</button>
      </div>
    </Modal>
  );
}

// Sites manager — sites are full records (name, address, type) created only
// by admin, each optionally assigned to one access-code holder.
function SitesManager({ onClose, onChanged, regionFilter = "", embedded = false }) {
  const [sites, setSites] = useState([]);
  const [codes, setCodes] = useState([]);
  const [types, setTypes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const empty = { name: "", address: "", location_type: "", assigned_code: "", region: "", province: "ON", active: true, postal_code: "", contact: "", phone: "", fax: "", email: "", hours: "", mailing_address: "" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [histFor, setHistFor] = useState(null);
  const [listMgr, setListMgr] = useState(null); // "types" | "regions" | "people" | "status"
  const [statusPrefix, setStatusPrefix] = useState("mkt-site-new"); // which form the status picker edits
  const [personSel, setPersonSel] = useState("");
  const [regionSel, setRegionSel] = useState(regionFilter);
  const [dateSel, setDateSel] = useState("");
  const [typeSel, setTypeSel] = useState("");

  const load = useCallback(async () => {
    const [s, c, t, g, p] = await Promise.all([
      api.get("/admin/sites"), api.get("/admin/codes"), api.get("/admin/location-types"), api.get("/admin/regions"),
      api.get("/admin/provinces"),
    ]);
    setSites(s.data);
    setCodes(c.data.filter((x) => x.active));
    setTypes(t.data);
    setRegions(g.data);
    setProvinces(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Type/City lists open in a small manage dialog (add / rename / delete)
  // instead of cluttering the site form with inline buttons.
  const listProps = (kind) => {
    const base = kind === "types" ? "/admin/location-types" : kind === "provinces" ? "/admin/provinces" : "/admin/regions";
    const noun = kind === "types" ? "type" : kind === "provinces" ? "province" : "city";
    return {
      items: kind === "types" ? types : kind === "provinces" ? provinces : regions,
      testPrefix: `mkt-manage-${kind}`,
      onAdd: async (name) => {
        try { await api.post(base, { name: name.trim() }); await load(); onChanged?.(); toast.success("Added"); }
        catch (e) { toast.error(e?.response?.data?.detail || `Could not add ${noun}`); throw e; }
      },
      onRename: async (it, name) => {
        try { await api.patch(`${base}/${it.id}`, { name: name.trim() }); await load(); onChanged?.(); toast.success("Renamed"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); throw e; }
      },
      onDelete: async (it) => {
        if (!window.confirm(`Delete the ${noun} "${it.name}"? Sites and people tagged with it keep the saved value.`)) return;
        await api.delete(`${base}/${it.id}`);
        await load(); onChanged?.(); toast.success("Deleted");
      },
    };
  };

  const add = async () => {
    if (!form.name.trim() || !form.address.trim() || !form.location_type) {
      toast.error("Name, address and type are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/sites", { ...form, active: form.active !== false });
      setForm(empty);
      await load(); onChanged?.();
      toast.success("Site added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add site");
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/sites/${editId}`, { ...editForm, active: editForm.active !== false });
      setEditId(null); setEditForm(null);
      await load(); onChanged?.();
      toast.success("Site updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update site");
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the site "${s.name}"? Existing submissions keep their saved details.`)) return;
    await api.delete(`/admin/sites/${s.id}`);
    await load(); onChanged?.();
  };

  const assignOptions = (current) => (
    <>
      <option value="">Unassigned</option>
      {codes.map((c) => <option key={c.code} value={c.code}>{c.name || c.code}</option>)}
      {current && !codes.some((c) => c.code === current) && (
        <option value={current}>{current} (inactive)</option>
      )}
    </>
  );

  const iconBtn = { height: 38, width: 38, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 8, cursor: "pointer", color: NAVY, fontFamily: "inherit" };

  const fields = (vals, setVals, prefix) => (
    <div style={{ display: "grid", gap: 8 }}>
      <div><span style={label}>Site name *</span>
        <input data-testid={`${prefix}-name`} style={input} placeholder="Malvern" value={vals.name}
          onChange={(e) => setVals({ ...vals, name: e.target.value })} /></div>
      <div><span style={label}>Address *</span>
        <input data-testid={`${prefix}-address`} style={input} placeholder="5639 Finch Ave East, 2nd Floor" value={vals.address}
          onChange={(e) => setVals({ ...vals, address: e.target.value })} /></div>
      <div><span style={label}>Postal code</span>
        <input data-testid={`${prefix}-postal`} style={input} placeholder="M1B 5S7" value={vals.postal_code || ""}
          onChange={(e) => setVals({ ...vals, postal_code: e.target.value })} /></div>
      <div><span style={label}>Office contact</span>
        <input data-testid={`${prefix}-contact`} style={input} placeholder="Contact person" value={vals.contact || ""}
          onChange={(e) => setVals({ ...vals, contact: e.target.value })} /></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div><span style={label}>Phone</span>
          <input data-testid={`${prefix}-phone`} style={input} inputMode="tel" placeholder="(416) 314-0261" value={vals.phone || ""}
            onChange={(e) => setVals({ ...vals, phone: fmtPhone(e.target.value) })} /></div>
        <div><span style={label}>Fax</span>
          <input data-testid={`${prefix}-fax`} style={input} inputMode="tel" placeholder="(416) 860-7588" value={vals.fax || ""}
            onChange={(e) => setVals({ ...vals, fax: fmtPhone(e.target.value) })} /></div>
      </div>
      <div><span style={label}>Email</span>
        <input data-testid={`${prefix}-email`} style={input} inputMode="email" placeholder="ODSP.Malvern@ontario.ca" value={vals.email || ""}
          onChange={(e) => setVals({ ...vals, email: e.target.value })} /></div>
      <div><span style={label}>Type *</span>
        <div style={{ display: "flex", gap: 6 }}>
          <select data-testid={`${prefix}-type`} style={{ ...input, appearance: "none", flex: 1 }} value={vals.location_type}
            onChange={(e) => setVals({ ...vals, location_type: e.target.value })}>
            <option value="">— Select —</option>
            {types.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
          <button data-testid={`${prefix}-type-manage`} title="Manage types (add, rename, delete)" style={iconBtn}
            onClick={() => setListMgr("types")}>
            <SlidersHorizontal style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
      <div>
        <span style={label}>City</span>
        <div style={{ display: "flex", gap: 6 }}>
          <select data-testid={`${prefix}-region`} style={{ ...input, appearance: "none", flex: 1 }} value={vals.region}
            onChange={(e) => setVals({ ...vals, region: e.target.value })}>
            <option value="">— No city —</option>
            {regions.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>
          <button data-testid={`${prefix}-region-manage`} title="Manage cities (add, rename, delete)" style={iconBtn}
            onClick={() => setListMgr("regions")}>
            <SlidersHorizontal style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
      <div>
        <span style={label}>Province</span>
        <div style={{ display: "flex", gap: 6 }}>
          <select data-testid={`${prefix}-province`} style={{ ...input, appearance: "none", flex: 1 }} value={vals.province || "ON"}
            onChange={(e) => setVals({ ...vals, province: e.target.value })}>
            {provinces.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
          <button data-testid={`${prefix}-province-manage`} title="Manage provinces (add, rename, delete)" style={iconBtn}
            onClick={() => setListMgr("provinces")}>
            <SlidersHorizontal style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
      <div><span style={label}>Assigned to</span>
        <div style={{ display: "flex", gap: 6 }}>
          <select data-testid={`${prefix}-assign`} style={{ ...input, appearance: "none", flex: 1 }} value={vals.assigned_code}
            onChange={(e) => setVals({ ...vals, assigned_code: e.target.value })}>
            {assignOptions(vals.assigned_code)}
          </select>
          <button data-testid={`${prefix}-assign-manage`} title="Manage people (add, edit, revoke)" style={iconBtn}
            onClick={() => setListMgr("people")}>
            <SlidersHorizontal style={{ width: 14, height: 14 }} />
          </button>
        </div></div>
      <div><span style={label}>Status</span>
        <div style={{ display: "flex", gap: 6 }}>
          <select data-testid={`${prefix}-active`} style={{ ...input, appearance: "none", flex: 1, fontWeight: 800, color: vals.active !== false ? GREEN : RED }}
            value={vals.active !== false ? "active" : "inactive"}
            onChange={(e) => setVals({ ...vals, active: e.target.value === "active" })}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button data-testid={`${prefix}-status-manage`} title="Set status" style={iconBtn}
            onClick={() => { setStatusPrefix(prefix); setListMgr("status"); }}>
            <SlidersHorizontal style={{ width: 14, height: 14 }} />
          </button>
        </div></div>
    </div>
  );

  const body = (
    <>
      <div style={{ background: "#FAF9F6", border: `1px solid ${BORDER}`, borderRadius: 10, padding: 12, marginBottom: 14 }}>
        {fields(form, setForm, "mkt-site-new")}
        <button data-testid="mkt-site-add" style={{ ...btn(GREEN), width: "100%", marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }} onClick={add} disabled={busy}>
          <Plus style={{ width: 14, height: 14 }} />Add site
        </button>
      </div>
      {(() => {
        const rl2 = regionSel.trim().toLowerCase();
        const visibleSites = sites.filter((s) => {
          if (rl2 && (s.region || "").toLowerCase() !== rl2) return false;
          if (personSel && s.assigned_code !== personSel) return false;
          if (dateSel && (s.month_visit || s.scheduled_date) !== dateSel) return false;
          if (typeSel && (s.location_type || "") !== typeSel) return false;
          return true;
        });
        const dateOptions = [...new Set(sites.map((s) => s.month_visit || s.scheduled_date).filter(Boolean))].sort();
        return (<>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <select data-testid="mkt-sites-region" value={regionSel} onChange={(e) => setRegionSel(e.target.value)}
          style={{ ...input, minWidth: 0, height: 34, appearance: "none" }}>
          <option value="">All cities</option>
          {regions.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
        </select>
        <select data-testid="mkt-sites-person" value={personSel} onChange={(e) => setPersonSel(e.target.value)}
          style={{ ...input, minWidth: 0, height: 34, appearance: "none" }}>
          <option value="">All people</option>
          {codes.map((c) => <option key={c.code} value={c.code}>{c.name || c.code}</option>)}
        </select>
        <select data-testid="mkt-sites-date" value={dateSel} onChange={(e) => setDateSel(e.target.value)}
          style={{ ...input, minWidth: 0, height: 34, appearance: "none" }}>
          <option value="">All visit dates</option>
          {dateOptions.map((d) => <option key={d} value={d}>{fmtDay(d)}</option>)}
        </select>
        <select data-testid="mkt-sites-type" value={typeSel} onChange={(e) => setTypeSel(e.target.value)}
          style={{ ...input, minWidth: 0, height: 34, appearance: "none" }}>
          <option value="">All types</option>
          {types.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>
      </div>
      {visibleSites.length === 0 && <div style={{ fontSize: 12, color: MUTED }}>{rl2 || personSel || dateSel || typeSel ? "No sites match these filters." : "No sites yet."}</div>}
      {visibleSites.map((s) => (
        <div key={s.id} data-testid={`mkt-site-row-${s.id}`} style={{ padding: "9px 2px", borderBottom: `1px solid ${BORDER}` }}>
          {editId === s.id ? (
            <>
              {fields(editForm, setEditForm, "mkt-site-edit")}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                <button data-testid="mkt-site-edit-cancel" style={btnGhost} onClick={() => { setEditId(null); setEditForm(null); }}>Cancel</button>
                <button data-testid="mkt-site-edit-save" style={btn(GREEN)} onClick={saveEdit} disabled={busy}>Save</button>
              </div>
            </>
          ) : (
            <>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: TEXT }}>
                {s.name}
                {s.location_type && (
                  <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 999, marginLeft: 6, textTransform: "uppercase", letterSpacing: ".04em", background: "#F1EFE9", color: NAVY, verticalAlign: "2px", whiteSpace: "nowrap" }}>{s.location_type}</span>
                )}
              </span>
              <button data-testid={`mkt-site-edit-${s.id}`} onClick={() => { setEditId(s.id); setEditForm({ name: s.name, address: s.address, location_type: s.location_type, assigned_code: s.assigned_code, region: s.region || "", province: s.province || "ON", active: s.active !== false, postal_code: s.postal_code || "", contact: s.contact || "", phone: s.phone || "", fax: s.fax || "", email: s.email || "", hours: s.hours || "", mailing_address: s.mailing_address || "" }); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, padding: 4, flexShrink: 0 }}><Pencil style={{ width: 13, height: 13 }} /></button>
              <button data-testid={`mkt-site-delete-${s.id}`} onClick={() => remove(s)}
                style={{ background: "none", border: "none", cursor: "pointer", color: RED, padding: 4, flexShrink: 0 }}><Trash2 style={{ width: 13, height: 13 }} /></button>
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 1, wordBreak: "break-word" }}>
              {s.address ? <AddressLines address={s.address} /> : "No address yet — edit to complete"}
            </div>
            {s.active === false && (
              <div style={{ marginTop: 5 }}>
                <span data-testid={`mkt-site-inactive-${s.id}`} style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, textTransform: "uppercase", letterSpacing: ".04em", background: "#F1EFE9", color: RED, whiteSpace: "nowrap" }}>
                  Inactive
                </span>
              </div>
            )}
            {(s.visits || []).length > 0 && (
              <div style={{ marginTop: 4 }}>
                <button data-testid={`mkt-site-history-${s.id}`} onClick={() => setHistFor(histFor === s.id ? null : s.id)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: NAVY, fontSize: 11, fontWeight: 700, textDecoration: "underline", padding: 0, fontFamily: "inherit" }}>
                  {histFor === s.id ? "Hide visit history" : `Visit history (${s.visits.length}) — last ${fmtDay(s.visits[0].date)} (${s.visits[0].kind === "verify" ? "verified" : "new poster"})`}
                </button>
                {histFor === s.id && (
                  <div style={{ marginTop: 4, background: "#FAF9F6", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "6px 10px" }}>
                    {s.visits.map((v, i) => (
                      <div key={i} style={{ fontSize: 11.5, color: TEXT, padding: "3px 0", borderBottom: i < s.visits.length - 1 ? `1px solid #EFEDE8` : "none" }}>
                        <b>{fmtDay(v.date)}</b> — {v.kind === "verify" ? "Verified existing poster" : "New poster put up"}{v.by ? ` · ${v.by}` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </>
          )}
        </div>
      ))}
        </>);
      })()}
    </>
  );

  return (
    <>
    {embedded ? body : <Modal title="Manage" onClose={onClose} testId="mkt-sites-dialog">{body}</Modal>}
    {listMgr === "types" && (
      <ListManager title="Location types" onClose={() => setListMgr(null)} {...listProps("types")} />
    )}
    {listMgr === "regions" && (
      <ListManager title="Cities" onClose={() => setListMgr(null)} {...listProps("regions")} />
    )}
    {listMgr === "provinces" && (
      <ListManager title="Provinces" onClose={() => setListMgr(null)} {...listProps("provinces")} />
    )}
    {listMgr === "people" && (
      <CodesManager onClose={() => { setListMgr(null); load(); onChanged?.(); }} />
    )}
    {listMgr === "status" && (
      <Modal title="Status" onClose={() => setListMgr(null)} testId="mkt-status-dialog">
        {[["active", "Active", GREEN], ["inactive", "Inactive", RED]].map(([v, t, col]) => (
          <button key={v} data-testid={`mkt-status-pick-${v}`}
            onClick={() => {
              const [sv, setSv] = statusPrefix === "mkt-site-edit" ? [editForm, setEditForm] : [form, setForm];
              setSv({ ...sv, active: v === "active" });
              setListMgr(null);
            }}
            style={{ ...input, width: "100%", marginBottom: 8, cursor: "pointer", fontWeight: 800, textAlign: "left", color: col, fontFamily: "inherit" }}>{t}</button>
        ))}
      </Modal>
    )}
    </>
  );
}

// Access codes manager — each code is a person's profile: name + phone +
// email (tap to call/email from admin), plus the code itself.
function CodesManager({ onClose, embedded = false }) {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    const c = await api.get("/admin/codes");
    setCodes(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/codes", form);
      setForm({ name: "", phone: "", email: "" });
      await load();
      toast.success(`Code ${res.data.code} created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create code");
    } finally { setBusy(false); }
  };
  const toggle = async (c) => {
    await api.patch(`/admin/codes/${c.id}`, { active: !c.active });
    await load();
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/codes/${editId}`, form);
      setEditId(null);
      setForm({ name: "", phone: "", email: "" });
      await load();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };

  const personFields = (vals, setVals, prefix) => (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <input data-testid={`${prefix}-name`} style={input} placeholder="Name (e.g. Anand)" value={vals.name}
          onChange={(e) => setVals({ ...vals, name: e.target.value })} />
        <input data-testid={`${prefix}-phone`} style={input} inputMode="tel" placeholder="(555) 555-9999" value={vals.phone}
          onChange={(e) => setVals({ ...vals, phone: fmtPhone(e.target.value) })} />
      </div>
      <input data-testid={`${prefix}-email`} style={input} inputMode="email" placeholder="Email" value={vals.email}
        onChange={(e) => setVals({ ...vals, email: e.target.value })} />
    </div>
  );

  const body = (
    <>
      <div style={{ background: "#FAF9F6", border: `1px solid ${BORDER}`, borderRadius: 10, padding: 12, marginBottom: 14 }}>
        {editId && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }} data-testid="mkt-codes-editing">
            <span style={{ fontSize: 11.5, fontWeight: 700, color: NAVY, userSelect: "none", WebkitTouchCallout: "none" }}>Editing {form.name || "person"}</span>
            <button data-testid="mkt-codes-copy-code" title="Copy the code"
              onClick={() => { const c = codes.find((x) => x.id === editId); if (c) { navigator.clipboard?.writeText(c.code); toast.success(`Code ${c.code} copied`); } }}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 24, padding: "0 9px", borderRadius: 7, border: `1px solid ${BORDER}`, background: "#fff", color: NAVY, fontSize: 11, fontWeight: 800, fontFamily: "monospace", letterSpacing: 1, cursor: "pointer", userSelect: "none", WebkitTouchCallout: "none" }}>
              {codes.find((x) => x.id === editId)?.code}<Copy style={{ width: 11, height: 11 }} />
            </button>
          </div>
        )}
        {personFields(form, setForm, "mkt-codes-new")}
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          {editId && (
            <button data-testid="mkt-codes-cancel-edit" style={{ ...btnGhost, flexShrink: 0 }}
              onClick={() => { setEditId(null); setForm({ name: "", phone: "", email: "" }); }}>Cancel</button>
          )}
          <button data-testid="mkt-codes-generate" style={{ ...btn(GREEN), flex: 1 }} onClick={editId ? saveEdit : generate} disabled={busy}>
            {editId ? "Save changes" : "Generate code"}
          </button>
        </div>
      </div>
      {(() => {
        const visibleCodes = codes;
        return (<>
      {visibleCodes.length === 0 && <div style={{ fontSize: 12, color: MUTED }}>No codes yet.</div>}
      {visibleCodes.map((c) => (
        <div key={c.id} data-testid={`mkt-code-row-${c.id}`} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 2px", borderBottom: `1px solid ${BORDER}`, opacity: c.active ? 1 : 0.5 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} data-testid={`mkt-code-name-${c.id}`}>{c.name}</span>
          <button data-testid={`mkt-code-edit-${c.id}`} title="Edit — fills the form above"
            onClick={() => { setEditId(c.id); setForm({ name: c.name, phone: c.phone || "", email: c.email || "" }); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, padding: 4, flexShrink: 0 }}><Pencil style={{ width: 13, height: 13 }} /></button>
          <button data-testid={`mkt-code-toggle-${c.id}`} onClick={() => toggle(c)}
            style={{ background: "none", border: "none", cursor: "pointer", color: c.active ? RED : GREEN, fontSize: 11, fontWeight: 700, textDecoration: "underline", padding: 4, flexShrink: 0 }}>
            {c.active ? "Revoke" : "Reactivate"}
          </button>
        </div>
      ))}
        </>);
      })()}
    </>
  );

  return embedded ? body : <Modal title="Access codes" onClose={onClose} testId="mkt-codes-dialog">{body}</Modal>;
}

// Renders a long address on two lines: "700 Lawrence Ave West, 3rd Floor" then
// "Suite 330, East Tower" — short addresses (≤2 parts) stay on one line.
function AddressLines({ address }) {
  const parts = (address || "").split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 2) return <>{address}</>;
  return (<>{parts.slice(0, 2).join(", ")}<br />{parts.slice(2).join(", ")}</>);
}

// Compact tap-a-day grid for a month — shared by the planner dialog and the
// scheduled-visits card. onPick(iso), with "" meaning "cleared".
function MonthDayGrid({ month, value, onPick, testPrefix }) {
  const lastDay = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  const firstDow = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  return (
    <div style={{ marginTop: 6, background: "#FAF9F6", border: `1px solid ${BORDER}`, borderRadius: 10, padding: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, marginBottom: 3 }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((w, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 9, fontWeight: 800, color: MUTED }}>{w}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const iso = `${month}-${String(d).padStart(2, "0")}`;
          const sel = value === iso;
          return (
            <button key={i} type="button" data-testid={`${testPrefix}-${d}`}
              onClick={() => onPick(sel ? "" : iso)}
              style={{
                height: 32, borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
                fontSize: 12.5, fontWeight: sel ? 800 : 600,
                border: sel ? `2px solid ${NAVY}` : `1px solid ${BORDER}`,
                background: sel ? NAVY : "#fff",
                color: sel ? "#fff" : TEXT,
              }}>
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Monthly visit planner — for the viewed month, decide per ACTIVE site:
// pick a visit date or skip the month. Nothing recurs automatically; a site
// left undecided pops the planner again the next time that month is viewed.
function PlanDialog({ month, region, onClose, onSaved }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dayPickFor, setDayPickFor] = useState(null); // site id with the day grid open
  const [fPerson, setFPerson] = useState("");
  const [fRegion, setFRegion] = useState(region || "");
  useEffect(() => {
    api.get(`/admin/plan?month=${month}`)
      .then((res) => setRows(res.data)).catch(() => toast.error("Could not load the planner"));
  }, [month]);

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/plan", {
        month,
        plans: rows.map((r) => ({ site_id: r.id, date: r.planned_date || "", skip: !!r.skipped })),
      });
      toast.success("Month planned");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the plan");
    } finally { setBusy(false); }
  };

  const lastDay = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();

  const gridFor = (r) => (
    <div data-testid={`mkt-plan-grid-${r.id}`}>
      <MonthDayGrid month={month} value={r.planned_date} testPrefix={`mkt-plan-day-${r.id}`}
        onPick={(iso) => {
          if (iso) { setRow(r.id, { planned_date: iso, skipped: false }); setDayPickFor(null); }
          else setRow(r.id, { planned_date: "" });
        }} />
    </div>
  );

  // In-dialog filters: city + staff dropdowns over the full month's plan
  const planRegions = [...new Set((rows || []).map((r) => r.region).filter(Boolean))].sort();
  if (fRegion && !planRegions.includes(fRegion)) planRegions.push(fRegion);
  const planPeople = Array.from(new Map(
    (rows || []).filter((r) => r.assigned_code).map((r) => [r.assigned_code, r.assigned_name || r.assigned_code])
  ).entries()).sort((a, b) => a[1].localeCompare(b[1]));
  const match = (r) =>
    (!fRegion || (r.region || "") === fRegion) &&
    (!fPerson || r.assigned_code === fPerson);
  const scheduled = (rows || []).filter((r) => r.planned_date && match(r));
  const skippedRows = (rows || []).filter((r) => !r.planned_date && r.skipped && match(r));
  const undecided = (rows || []).filter((r) => !r.planned_date && !r.skipped && match(r));

  return (
    <Modal title={`Plan visits — ${monthLabel(month)}`} onClose={onClose} testId="mkt-plan-dialog">
      {(rows || []).length > 3 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <select data-testid="mkt-plan-region" value={fRegion} onChange={(e) => setFRegion(e.target.value)}
            style={{ ...input, width: 118, flexShrink: 0, height: 34, appearance: "none" }}>
            <option value="">All cities</option>
            {planRegions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select data-testid="mkt-plan-person" value={fPerson} onChange={(e) => setFPerson(e.target.value)}
            style={{ ...input, flex: 1, minWidth: 0, height: 34, appearance: "none" }}>
            <option value="">All people</option>
            {planPeople.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>
      )}
      {rows === null ? (
        <div style={{ fontSize: 12, color: MUTED }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: MUTED }}>No active sites.</div>
      ) : (
        <>
          {scheduled.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: GREEN, marginBottom: 4 }}>Scheduled</div>
              {scheduled.map((r) => (
                <div key={r.id} data-testid={`mkt-plan-scheduled-${r.id}`} style={{ padding: "5px 2px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: TEXT, whiteSpace: "nowrap", overflowX: "auto", scrollbarWidth: "none" }}>
                      <b>{fmtDay(r.planned_date)}</b> — {r.name} · {r.assigned_name || "Unassigned"}{r.region ? ` · ${r.region}` : ""}
                    </span>
                    <button data-testid={`mkt-plan-change-${r.id}`} title="Pick a different day"
                      onClick={() => setDayPickFor(dayPickFor === r.id ? null : r.id)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: NAVY, fontSize: 11, fontWeight: 700, textDecoration: "underline", padding: 2, flexShrink: 0, fontFamily: "inherit" }}>Change</button>
                    <button data-testid={`mkt-plan-skipbtn-${r.id}`} title="Cancel this visit for the month"
                      onClick={() => { setRow(r.id, { skipped: true, planned_date: "" }); setDayPickFor(null); }}
                      style={{ background: "none", border: "none", cursor: "pointer", color: RED, fontSize: 11, fontWeight: 700, textDecoration: "underline", padding: 2, flexShrink: 0, fontFamily: "inherit" }}>Skip</button>
                  </div>
                  {dayPickFor === r.id && gridFor(r)}
                </div>
              ))}
            </div>
          )}
          {undecided.length === 0 ? (
            <div style={{ fontSize: 12, color: MUTED, padding: "6px 0" }} data-testid="mkt-plan-done">
              {(fPerson || fRegion) && (rows || []).some((r) => !r.planned_date && !r.skipped)
                ? "No undecided sites match these filters — clear them to see everything."
                : `All active sites are covered for ${monthLabel(month)} — scheduled above, skipped below. Use Change or Skip to adjust.`}
            </div>
          ) : (
            undecided.map((r) => (
              <div key={r.id} data-testid={`mkt-plan-row-${r.id}`} style={{ padding: "9px 2px", borderBottom: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{r.name}</div>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 1 }}>
                  {r.assigned_name || "Unassigned"}{r.region ? ` · ${r.region}` : ""}
                  {r.last_visit
                    ? ` · last visit ${fmtDay(r.last_visit.date)} (${r.last_visit.kind === "verify" ? "verified" : "new poster"})`
                    : " · no visits yet"}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
                  <button type="button" data-testid={`mkt-plan-pick-${r.id}`} disabled={!!r.skipped}
                    onClick={() => setDayPickFor(dayPickFor === r.id ? null : r.id)}
                    style={{ ...input, flex: 1, textAlign: "left", cursor: r.skipped ? "default" : "pointer", fontWeight: 700, fontFamily: "inherit", background: "#fff", color: r.planned_date ? TEXT : MUTED, opacity: r.skipped ? 0.45 : 1 }}>
                    {r.planned_date ? fmtDay(r.planned_date) : "Pick a day…"}
                  </button>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: TEXT, whiteSpace: "nowrap" }}>
                    <input type="checkbox" data-testid={`mkt-plan-skip-${r.id}`} checked={!!r.skipped}
                      onChange={(e) => { setRow(r.id, { skipped: e.target.checked, planned_date: e.target.checked ? "" : r.planned_date }); if (e.target.checked) setDayPickFor(null); }}
                      style={{ width: 16, height: 16, accentColor: RED }} />
                    Skip
                  </label>
                </div>
                {dayPickFor === r.id && !r.skipped && gridFor(r)}
              </div>
            ))
          )}
          {skippedRows.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: MUTED, marginBottom: 4 }}>Skipped this month</div>
              {skippedRows.map((r) => (
                <div key={r.id} data-testid={`mkt-plan-skipped-${r.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px" }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: MUTED, whiteSpace: "nowrap", overflowX: "auto", scrollbarWidth: "none" }}>{r.name} · {r.assigned_name || "Unassigned"}</span>
                  <button data-testid={`mkt-plan-unskip-${r.id}`} onClick={() => setRow(r.id, { skipped: false })}
                    style={{ background: "none", border: "none", cursor: "pointer", color: NAVY, fontSize: 11, fontWeight: 700, textDecoration: "underline", padding: 2, fontFamily: "inherit" }}>Undo</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button data-testid="mkt-plan-later" style={{ ...btnGhost, flex: 1 }} onClick={onClose}>Decide later</button>
        <button data-testid="mkt-plan-save" style={{ ...btn(GREEN), flex: 1 }} onClick={save} disabled={busy || !rows}>{busy ? "Saving…" : "Save plan"}</button>
      </div>
    </Modal>
  );
}

// One "Manage" dialog with two tabs — Sites (records, types, cities) and
// People (access codes) — so admin manages everything in one place.
function ManageDialog({ onClose, onChanged, region = "" }) {
  const [tab, setTab] = useState("sites");
  const tabBtn = (id, label) => (
    <button key={id} data-testid={`mkt-manage-tab-${id}`} onClick={() => setTab(id)}
      style={{ flex: 1, height: 34, borderRadius: 9, border: `1px solid ${BORDER}`, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 800,
        background: tab === id ? NAVY : "#fff", color: tab === id ? "#fff" : TEXT, transition: "background-color .15s, color .15s" }}>{label}</button>
  );
  return (
    <Modal title="Manage" onClose={onClose} testId="mkt-sites-dialog">
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {tabBtn("sites", "Sites")}
        {tabBtn("people", "People")}
      </div>
      {tab === "sites"
        ? <SitesManager embedded onChanged={onChanged} regionFilter={region} />
        : <CodesManager embedded />}
    </Modal>
  );
}

export default function MarketingPanel({ refreshKey = 0 }) {
  const [month, setMonth] = useState(() => new Date().toLocaleDateString("en-CA").slice(0, 7));
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [options, setOptions] = useState({ sites: [], location_types: [] });
  const [codes, setCodes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState("");
  const [topups, setTopups] = useState([]);
  const [planRows, setPlanRows] = useState([]);
  const [q, setQ] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [personFilter, setPersonFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [photoFor, setPhotoFor] = useState(null);
  const [managing, setManaging] = useState(null); // "sites" | "codes"
  const [saving, setSaving] = useState(false);
  const [tpCode, setTpCode] = useState("");
  const [tpDate, setTpDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [tpAmt, setTpAmt] = useState("");
  const [tpBusy, setTpBusy] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false); // scheduled visits card collapsed by default
  const [cardPickFor, setCardPickFor] = useState(null); // site id with the day grid open on the card

  const rescheduleVisit = async (v, iso) => {
    setCardPickFor(null);
    if (!iso) return;
    try {
      await api.post("/admin/plan", { month, plans: [{ site_id: v.id, date: iso }] });
      toast.success(`${v.name} moved to ${fmtDay(iso)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change the date");
    }
  };

  const skipVisit = async (v) => {
    if (!window.confirm(`Skip ${v.name} for ${monthLabel(month)}? It stays active and comes back for scheduling next month.`)) return;
    await api.post("/admin/plan", { month, plans: [{ site_id: v.id, skip: true }] });
    toast.success(`${v.name} skipped for ${monthLabel(month)}`);
    await load();
  };

  const months = useMemo(() => {
    // 3 months ahead (plan visits before the 1st) + current + 12 back.
    const out = [];
    const d = new Date(); d.setDate(15); d.setMonth(d.getMonth() + 3);
    for (let i = 0; i < 16; i++) { out.push(d.toISOString().slice(0, 7)); d.setMonth(d.getMonth() - 1); }
    return out;
  }, []);

  const load = useCallback(async () => {
    const rq = region ? `&region=${encodeURIComponent(region)}` : "";
    const [sum, recs, opts, cds, tps, rgs, pln] = await Promise.all([
      api.get(`/admin/summary?month=${month}${rq}`),
      api.get(`/admin/submissions?month=${month}${rq}`),
      api.get("/admin/options"),
      api.get("/admin/codes"),
      api.get(`/admin/topups?month=${month}${rq}`),
      api.get("/admin/regions"),
      api.get(`/admin/plan?month=${month}${rq}`),
    ]);
    setSummary(sum.data); setRecords(recs.data); setOptions(opts.data);
    setCodes(cds.data.filter((c) => c.active));
    setTopups(tps.data);
    setRegions(rgs.data);
    setPlanRows(pln.data);
  }, [month, region, refreshKey]);

  useEffect(() => { load().catch(() => toast.error("Could not load marketing data")); }, [load]);

  // Planner pops EVERY time the viewed month changes to a current/future
  // month that still has undecided active sites — and also whenever the
  // dashboard Refresh button is tapped, so no month stays unplanned by
  // accident. "Plan visits" opens it manually anytime.
  useEffect(() => {
    const cur = new Date().toLocaleDateString("en-CA").slice(0, 7);
    if (month < cur) return;
    api.get(`/admin/plan?month=${month}${region ? `&region=${encodeURIComponent(region)}` : ""}`)
      .then((res) => { if (res.data.some((s) => s.needs_decision)) setPlanOpen(true); })
      .catch(() => {});
  }, [month, refreshKey, region]);

  // Region cascades top-down: site/person dropdown options only list what
  // belongs to the selected region (site's own region or its assignee's).
  const rl = region.trim().toLowerCase();
  const siteOptions = options.sites.filter((s) =>
    !rl || (s.region || "").toLowerCase() === rl);
  const peopleOptions = codes.filter((c) => {
    // Site selection wins: the site's assignee shows regardless of city.
    if (siteFilter) {
      const s = options.sites.find((x) => x.id === siteFilter);
      if (s?.assigned_code) return c.code === s.assigned_code;
    }
    // City filter: people assigned to a site in that city.
    if (rl && !options.sites.some((s) => s.assigned_code === c.code && (s.region || "").toLowerCase() === rl)) return false;
    return true;
  });
  // Top-up "who was paid" follows the city of the person's sites.
  const topupPeople = codes.filter((c) => !rl ||
    options.sites.some((s) => s.assigned_code === c.code && (s.region || "").toLowerCase() === rl));

  useEffect(() => {
    if (siteFilter && !siteOptions.some((s) => s.id === siteFilter)) setSiteFilter("");
    if (personFilter && !peopleOptions.some((c) => c.code === personFilter)) setPersonFilter("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rl, options.sites, codes]);

  const shown = useMemo(() => {    let out = records;
    if (siteFilter) out = out.filter((r) => r.site_id === siteFilter);
    if (personFilter) out = out.filter((r) => r.code === personFilter);
    const needle = q.trim().toLowerCase();
    if (!needle) return out;
    return out.filter((r) =>
      [r.date, r.address, r.site, r.location_type, r.submitted_by].join(" ").toLowerCase().includes(needle));
  }, [records, q, siteFilter, personFilter]);

  // This month's scheduled visits (from the planner), with done/overdue state.
  const todayYMD = new Date().toISOString().slice(0, 10);
  const scheduledVisits = useMemo(() => {
    const visitedSiteIds = new Set(records.map((r) => r.site_id));
    return planRows
      .filter((r) => r.planned_date)
      .filter((r) => (!siteFilter || r.id === siteFilter) && (!personFilter || r.assigned_code === personFilter))
      .sort((a, b) => a.planned_date.localeCompare(b.planned_date))
      .map((r) => ({
        ...r,
        visited: visitedSiteIds.has(r.id),
        overdue: r.planned_date < todayYMD && !visitedSiteIds.has(r.id),
      }));
  }, [planRows, records, todayYMD, siteFilter, personFilter]);

  const approve = async (r) => {
    await api.post(`/admin/submissions/${r.id}/approve`);
    await load();
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/submissions/${editing.id}`, {
        date: editing.date, address: editing.address, site: editing.site,
        location_type: editing.location_type, amount: Number(editing.amount),
        kind: editing.kind || "new",
      });
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const addTopup = async () => {
    if (!tpCode) { toast.error("Choose who you paid"); return; }
    if (!tpDate) { toast.error("Pick the payment date"); return; }
    if (!(Number(tpAmt) > 0)) { toast.error("Enter an amount greater than 0"); return; }
    setTpBusy(true);
    try {
      await api.post("/admin/topups", { code: tpCode, date: tpDate, amount: Number(tpAmt), month });
      setTpAmt("");
      toast.success("Top-up added");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add top-up");
    } finally { setTpBusy(false); }
  };

  const deleteTopup = async (t) => {
    if (!window.confirm(`Delete the ${money(t.amount)} top-up for ${t.name || t.code} on ${fmtDay(t.date)}?`)) return;
    await api.delete(`/admin/topups/${t.id}`);
    toast.success("Top-up deleted");
    await load();
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the ${fmtDay(r.date)} submission for ${r.address}? This cannot be undone.`)) return;
    await api.delete(`/admin/submissions/${r.id}`);
    toast.success("Deleted");
    setEditing(null);
    await load();
  };

  const Tile = ({ k, v, sub, color }) => (
    <div data-testid={`mkt-tile-${k.toLowerCase()}`} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,.05)" }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", color: "#5B6B62", textTransform: "uppercase" }}>{k}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || TEXT, marginTop: 4 }}>{v}</div>
      <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{sub}</div>
    </div>
  );

  return (
    <div data-testid="marketing-panel">
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 8 }}>
        <select data-testid="mkt-region" value={region} onChange={(e) => setRegion(e.target.value)} style={{ ...input, flex: 1, minWidth: 0, height: 34, fontWeight: 800, appearance: "none" }}>
          <option value="">All cities</option>
          {regions.map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
        </select>
        <select data-testid="mkt-month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ ...input, width: "auto", flexShrink: 0, height: 34 }}>
          {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        <button data-testid="mkt-export-pdf" title="Export PDF" style={{ ...btnGhost, flexShrink: 0, width: 36, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => window.open(`${BACKEND}/api/marketing/admin/export.pdf?month=${month}&region=${encodeURIComponent(region)}&token=${getToken()}`, "_blank")}>
          <FileDown style={{ width: 15, height: 15 }} />
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <select data-testid="mkt-filter-site" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}
          style={{ ...input, flex: 1, minWidth: 0, height: 34, appearance: "none" }}>
          <option value="">All sites</option>
          {siteOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select data-testid="mkt-filter-person" value={personFilter} onChange={(e) => setPersonFilter(e.target.value)}
          style={{ ...input, flex: 1, minWidth: 0, height: 34, appearance: "none" }}>
          <option value="">All people</option>
          {peopleOptions.map((c) => <option key={c.code} value={c.code}>{c.name || c.code}</option>)}
        </select>
      </div>

      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 12 }}>
          <Tile k="Submitted" v={money(summary.submitted_total)} sub={`${summary.submitted_count} record${summary.submitted_count === 1 ? "" : "s"}`} />
          <Tile k="Approved" v={money(summary.approved_total)} sub={`${summary.approved_count} record${summary.approved_count === 1 ? "" : "s"}`} color={GREEN} />
          <Tile k="Pending" v={money(summary.pending_total)} sub="awaiting payment" color={AMBER} />
          <Tile k="Paid" v={money(summary.paid_total)} sub={`${summary.paid_count} top-up${summary.paid_count === 1 ? "" : "s"}`} />
        </div>
      )}

      {scheduledVisits.length > 0 && (() => {
        const nOverdue = scheduledVisits.filter((v) => v.overdue).length;
        const nUpcoming = scheduledVisits.filter((v) => !v.overdue && !v.visited).length;
        const nDone = scheduledVisits.filter((v) => v.visited).length;
        return (
          <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "12px 14px", marginBottom: 12, boxShadow: "0 1px 3px rgba(0,0,0,.05)" }} data-testid="mkt-scheduled">
            <button data-testid="mkt-scheduled-toggle" onClick={() => setSchedOpen(!schedOpen)}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: MUTED }}>
                Scheduled ({scheduledVisits.length})
              </span>
              <span style={{ flex: 1, textAlign: "left", fontSize: 10, fontWeight: 700, color: nOverdue ? RED : MUTED }}>
                {[
                  nOverdue ? `${nOverdue} overdue` : "",
                  nUpcoming ? `${nUpcoming} upcoming` : "",
                  nDone ? `${nDone} done` : "",
                ].filter(Boolean).join(" · ")}
              </span>
              <span style={{ fontSize: 12, color: MUTED }}>{schedOpen ? "▾" : "▸"}</span>
            </button>
            {schedOpen && scheduledVisits.map((v) => {
              // Only show the details not already pinned by active filters —
              // filtered views stay clean instead of repeating themselves.
              const parts = [];
              if (!siteFilter) parts.push(v.name);
              if (!personFilter) parts.push(v.assigned_name || "Unassigned");
              if (!rl && !siteFilter && v.region) parts.push(v.region);
              return (
              <div key={v.id} data-testid={`mkt-scheduled-${v.id}`} style={{ padding: "7px 2px", borderBottom: `1px solid #EFEDE8` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <b style={{ flex: 1, fontSize: 12.5, color: TEXT }}>{fmtDay(v.planned_date)}</b>
                  {v.visited ? (
                    <span style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: GREEN, flexShrink: 0 }}>Done</span>
                  ) : v.overdue ? (
                    <span style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: RED, flexShrink: 0 }}>Overdue</span>
                  ) : (
                    <span style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: AMBER, flexShrink: 0 }}>Upcoming</span>
                  )}
                  <button data-testid={`mkt-card-change-${v.id}`} onClick={() => setCardPickFor(cardPickFor === v.id ? null : v.id)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: NAVY, fontSize: 11, fontWeight: 700, textDecoration: "underline", padding: 2, flexShrink: 0, fontFamily: "inherit" }}>Change</button>
                  <button data-testid={`mkt-card-skip-${v.id}`} onClick={() => skipVisit(v)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: RED, fontSize: 11, fontWeight: 700, textDecoration: "underline", padding: 2, flexShrink: 0, fontFamily: "inherit" }}>Skip</button>
                </div>
                {parts.length > 0 && (
                  <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>
                    {parts.join(" · ")}
                  </div>
                )}
                {cardPickFor === v.id && (
                  <MonthDayGrid month={month} value={v.planned_date} testPrefix={`mkt-card-day-${v.id}`}
                    onPick={(iso) => rescheduleVisit(v, iso)} />
                )}
              </div>
              );
            })}
          </div>
        );
      })()}

      <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.05)" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "12px 14px", borderBottom: `1px solid ${BORDER}` }}>
          <input data-testid="mkt-search" style={{ ...input, height: 38, borderRadius: 10 }} placeholder="Search date, address, site…"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <span style={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>{shown.length} of {records.length}</span>
        </div>
        {shown.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: MUTED, fontSize: 12 }} data-testid="mkt-empty">No submissions this month.</div>
        ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr>
                {["Date", "Address", "Site · Type", "By", "Photo", "Status", "Amount", "Actions"].map((h) => (
                  <th key={h} style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: MUTED, textAlign: h === "Amount" ? "right" : "left", padding: "8px 10px", borderBottom: `1px solid ${BORDER}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const status = r.status || "pending";
                return (
                  <tr key={r.id} data-testid={`mkt-row-${r.id}`}>
                    <td style={{ padding: "9px 10px", borderBottom: `1px solid #EFEDE8`, fontSize: 12.5, whiteSpace: "nowrap" }}>{fmtDay(r.date)}</td>
                    <td style={{ padding: "9px 10px", borderBottom: `1px solid #EFEDE8`, fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>{r.address}</td>
                    <td style={{ padding: "9px 10px", borderBottom: `1px solid #EFEDE8`, fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>{r.site} · {r.location_type}</td>
                    <td style={{ padding: "9px 10px", borderBottom: `1px solid #EFEDE8`, fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>{r.submitted_by || "—"}</td>
                    <td style={{ padding: "9px 10px", borderBottom: `1px solid #EFEDE8` }}>
                      {r.has_photo ? (
                        <button data-testid={`mkt-photo-${r.id}`} onClick={() => setPhotoFor(r)}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, height: 24, padding: "0 9px", borderRadius: 7, border: `1px solid ${BORDER}`, background: "#FAF9F6", color: NAVY, fontSize: 10.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                          <ImageIcon style={{ width: 11, height: 11 }} /> View
                        </button>
                      ) : <span style={{ color: MUTED, fontSize: 11 }}>—</span>}
                    </td>
                    <td style={{ padding: "9px 10px", borderBottom: `1px solid #EFEDE8`, whiteSpace: "nowrap" }}>
                      <StatusPill status={status} />
                      <span data-testid={`mkt-kind-${r.id}`} style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginLeft: 5 }}>
                        {r.kind === "verify" ? "Verified" : "New"}
                      </span>
                    </td>
                    <td style={{ padding: "9px 10px", borderBottom: `1px solid #EFEDE8`, textAlign: "right", fontSize: 12.5, fontWeight: status === "pending" ? 600 : 700, color: status === "pending" ? TEXT : DARK_GREEN, fontVariantNumeric: "tabular-nums" }}>{money(r.amount)}</td>
                    <td style={{ padding: "9px 10px", borderBottom: `1px solid #EFEDE8`, whiteSpace: "nowrap" }}>
                      {status === "pending" && (
                        <button data-testid={`mkt-approve-${r.id}`} onClick={() => approve(r)}
                          style={{ height: 26, padding: "0 10px", background: GREEN, color: "#fff", border: "none", borderRadius: 7, fontSize: 10.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginRight: 6 }}>
                          Approve
                        </button>
                      )}
                      <button data-testid={`mkt-edit-${r.id}`} onClick={() => setEditing({ ...r })}
                        style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, padding: 4 }}>
                        <Pencil style={{ width: 14, height: 14 }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: 14, marginTop: 12, boxShadow: "0 1px 3px rgba(0,0,0,.05)" }} data-testid="mkt-topups">
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: MUTED, marginBottom: 8 }}>Add top-up</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
          <select data-testid="mkt-topup-person" style={{ ...input, appearance: "none" }} value={tpCode} onChange={(e) => setTpCode(e.target.value)}>
            <option value="">— Who was paid —</option>
            {topupPeople.map((c) => <option key={c.code} value={c.code}>{c.name || c.code}</option>)}
          </select>
          <input data-testid="mkt-topup-date" type="date" style={input} value={tpDate} onChange={(e) => setTpDate(e.target.value)} />
          <input data-testid="mkt-topup-amount" inputMode="decimal" placeholder="Amount ($)" style={input} value={tpAmt} onChange={(e) => setTpAmt(e.target.value)} />
          <button data-testid="mkt-topup-add" style={btn(GREEN)} onClick={addTopup} disabled={tpBusy}>
            {tpBusy ? "Adding…" : "Add top-up"}
          </button>
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: MUTED, margin: "14px 0 6px" }}>This month&apos;s top-ups</div>
        {topups.length === 0 ? (
          <div style={{ fontSize: 12, color: MUTED }} data-testid="mkt-topups-empty">No top-ups yet for {monthLabel(month)}.</div>
        ) : (
          topups.map((t) => (
            <div key={t.id} data-testid={`mkt-topup-row-${t.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 2px", borderBottom: `1px solid #EFEDE8` }}>
              <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ fontWeight: 800 }}>{t.name || t.code}</span> · {money(t.amount)} · {fmtDay(t.date)}{t.note ? ` · ${t.note}` : ""}
              </div>
              <button data-testid={`mkt-topup-delete-${t.id}`} onClick={() => deleteTopup(t)}
                style={{ background: "none", border: "none", cursor: "pointer", color: RED, padding: 4, flexShrink: 0 }}>
                <Trash2 style={{ width: 13, height: 13 }} />
              </button>
            </div>
          ))
        )}
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "nowrap" }}>
        <button data-testid="mkt-plan-open" style={{ ...btnGhostRow, flex: 1, padding: "0 6px", fontSize: 11.5 }} onClick={() => setPlanOpen(true)}>
          <Calendar style={{ width: 12, height: 12, flexShrink: 0 }} />Plan visits
        </button>
        <button data-testid="mkt-manage-sites" style={{ ...btnGhostRow, flex: 1, padding: "0 6px", fontSize: 11.5 }} onClick={() => setManaging("sites")}>
          <MapPin style={{ width: 12, height: 12, flexShrink: 0 }} />Manage
        </button>
      </div>

      {planOpen && (
        <PlanDialog month={month} region={region}
          onClose={() => setPlanOpen(false)}
          onSaved={async () => { setPlanOpen(false); await load(); }} />
      )}

      {managing === "sites" && (
        <ManageDialog onClose={() => setManaging(null)} onChanged={load} region={region} />
      )}

      {photoFor && (
        <Modal title={`${photoFor.address} — ${fmtDay(photoFor.date)}`} onClose={() => setPhotoFor(null)} testId="mkt-photo-modal">
          <img src={`${BACKEND}/api/marketing/photo/${photoFor.id}.jpg`} alt="Poster" style={{ width: "100%", borderRadius: 10 }} />
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit submission — ${fmtDay(editing.date)}`} onClose={() => setEditing(null)} testId="mkt-edit-dialog">
          <div style={{ display: "grid", gap: 10 }}>
            <div><span style={label}>Date</span>
              <input data-testid="mkt-edit-date" type="date" style={input} value={editing.date || ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
            <div><span style={label}>Site</span>
              <select data-testid="mkt-edit-site" style={{ ...input, appearance: "none" }} value={editing.site || ""}
                onChange={(e) => {
                  const s = options.sites.find((x) => x.name === e.target.value);
                  setEditing({
                    ...editing,
                    site: e.target.value,
                    ...(s ? { address: s.address || editing.address, location_type: s.location_type || editing.location_type } : {}),
                  });
                }}>
                <option value="">— Please select —</option>
                {options.sites.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                {editing.site && !options.sites.some((s) => s.name === editing.site) && (
                  <option value={editing.site}>{editing.site} (removed)</option>
                )}
              </select></div>
            <div><span style={label}>Address</span>
              <input data-testid="mkt-edit-address" style={input} value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></div>
            <div><span style={label}>Type of location</span>
              <select data-testid="mkt-edit-type" style={{ ...input, appearance: "none" }} value={editing.location_type || ""} onChange={(e) => setEditing({ ...editing, location_type: e.target.value })}>
                <option value="">— Please select —</option>
                {options.location_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><span style={label}>Amount</span>
              <input data-testid="mkt-edit-amount" inputMode="decimal" style={input} value={editing.amount ?? ""} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} /></div>
            <div><span style={label}>Visit type</span>
              <select data-testid="mkt-edit-kind" style={{ ...input, appearance: "none" }} value={editing.kind || "new"} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                <option value="new">New poster put up</option>
                <option value="verify">Verified existing poster</option>
              </select></div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button data-testid="mkt-edit-delete" onClick={() => remove(editing)} style={{ ...btn("#fff"), color: RED, border: `1px solid ${RED}`, flex: 1 }}>Delete</button>
            <button data-testid="mkt-edit-cancel" style={{ ...btnGhost, flex: 1 }} onClick={() => setEditing(null)}>Cancel</button>
            <button data-testid="mkt-edit-save" style={{ ...btn(GREEN), flex: 1 }} onClick={saveEdit} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
